import {definePreset} from "@primevue/themes"
import Aura from "@primevue/themes/aura"
// https://www.figma.com/design/eCYJUPzN0LLFQa9UDyYejq/Expirenza-Admin-UI-Kit?node-id=173-479&p=f&t=4OzX3YFrleFoz5tM-0
const palette = {
  neutral: {
    50: "#F9FAFB",
    100: "#F3F4F6",
    200: "#E5E7EB",
    300: "#D2D5DB",
    400: "#9AA1AD",
    500: "#6B7281",
    600: "#4C5564",
    700: "#384151",
    800: "#202937",
    900: "#121827",
    950: "#030712",
  },
  red: {
    50: "#FCF3F2",
    100: "#FAE3E2",
    200: "#F7CCCB",
    300: "#F4A6A5",
    400: "#EE6E6C",
    500: "#E64341",
    600: "#D42422",
    700: "#B11E1B",
    800: "#911F1C",
    900: "#77221F",
    950: "#400E0C",
  },
  pink: {
    50: "#FBF2F7",
    100: "#F9E8F2",
    200: "#F5D0E7",
    300: "#F1A9D3",
    400: "#E96EB3",
    500: "#E34798",
    600: "#D32475",
    700: "#B51A5B",
    800: "#951B4C",
    900: "#7B1E42",
    950: "#4A0D24",
  },
  orange: {
    50: "#FEF7EE",
    100: "#FCEED7",
    200: "#F9D8AD",
    300: "#F7BB76",
    400: "#F28F36",
    500: "#F17313",
    600: "#E25706",
    700: "#BA420D",
    800: "#933615",
    900: "#753017",
    950: "#3E160A",
  },
  indigo: {
    50: "#F0F4FD",
    100: "#E3EAFC",
    200: "#CCD8F9",
    300: "#ADBDF4",
    400: "#8C9AED",
    500: "#7079E4",
    600: "#5F5FD9",
    700: "#4745BD",
    800: "#3B3A99",
    900: "#363879",
    950: "#201F47",
  },
  purple: {
    50: "#FBF4FE",
    100: "#F7E8FE",
    200: "#F0D1FC",
    300: "#E8ABFB",
    400: "#DD72FA",
    500: "#CF3EF3",
    600: "#B715D6",
    700: "#9914B1",
    800: "#7E178F",
    900: "#691D74",
    950: "#44084C",
  },
  emerald: {
    50: "#EFFCF5",
    100: "#D8F9E6",
    200: "#B6F2D2",
    300: "#86E6B8",
    400: "#55D197",
    500: "#44B982",
    600: "#35966A",
    700: "#2A7858",
    800: "#235F47",
    900: "#1E4D3C",
    950: "#0E2B22",
  },
  // success
  green: {
    50: "#F2FDF5",
    100: "#E2FBE8",
    200: "#C6F6D2",
    300: "#99EFAE",
    400: "#66DC7E",
    500: "#4FC65F",
    600: "#3EA34B",
    700: "#33803F",
    800: "#2B6536",
    900: "#26532F",
    950: "#112D18",
  },
  lime: {
    50: "#F8FEE9",
    100: "#EFFCCF",
    200: "#DFF8A4",
    300: "#C7F26C",
    400: "#ABE435",
    500: "#8FCC21",
    600: "#6FA317",
    700: "#557C18",
    800: "#456219",
    900: "#3C531B",
    950: "#1E2E0A",
  },
  cyan: {
    50: "#EFFDFF",
    100: "#D7F9FD",
    200: "#B4F2FB",
    300: "#80E7FA",
    400: "#4BD0EE",
    500: "#3FB5D7",
    600: "#3190B4",
    700: "#2A7392",
    800: "#275D76",
    900: "#244D62",
    950: "#153243",
  },
  // info
  blue: {
    50: "#F0F6FE",
    100: "#DEEAFC",
    200: "#C4DAFC",
    300: "#9AC4FD",
    400: "#65A0FD",
    500: "#447DFC",
    600: "#2D5CF2",
    700: "#2346DD",
    800: "#223BB1",
    900: "#233889",
    950: "#192452",
  },
  // warning
  yellow: {
    50: "#FEFCEA",
    100: "#FDF9C8",
    200: "#FCF194",
    300: "#FBE152",
    400: "#F5CA0F",
    500: "#E6B313",
    600: "#C68A0B",
    700: "#9C630F",
    800: "#804E13",
    900: "#6C4018",
    950: "#3E210B",
  },
}

export const themePreset = definePreset(Aura, {
  //Your customizations, see the following sections for examples
  // https://primevue.org/theming/styled/#presets
  primitive: {
    ...palette,
    surface: palette.neutral,
    stone: palette.neutral,
    zinc: palette.neutral,
    gray: palette.neutral,
    slate: palette.neutral,
    fuchsia: palette.purple,
    violet: palette.purple,
    rose: palette.pink,
    teal: palette.cyan,
    sky: palette.blue,
    amber: palette.yellow,
  },
  semantic: {
    primary: palette.indigo,
    formField: {
      borderRadius: "8px",
    },
  },
  components: {
    button: {
      paddingX: "16px",
      paddingY: "8px",
      labelFontWeight: "600",
      colorScheme: {
        light: {
          link: {
            color: "{primary.600}",
            hover: {
              color: "{primary.700}",
            },
            active: {
              color: "{primary.800}",
            },
          },
        },
        dark: {
          link: {
            color: "{primary.600}",
            hover: {
              color: "{primary.700}",
            },
            active: {
              color: "{primary.800}",
            },
          },
        },
      },
    },
    inputtext: {
      paddingX: "12px",
      paddingY: "10px",
      focusRingWidth: "2px",
    },
    drawer: {
      background: "#FFFFFF",
      borderColor: "#FFFFFF",
    },
    progressbar: {
      value: {
        background: "{primary.500}",
      },
    },
    textarea: {
      invalid: {
        borderColor: palette.red[500],
        placeholderColor: palette.red[500],
      },
      lg: {
        fontSize: "16px",
      },
      sm: {
        fontSize: "16px",
      },
    },
    dialog: {
      header: {
        padding: "16px",
      },
      content: {
        padding: "0px",
      },
    },
    toast: {
      borderRadius: "8px",
      summaryFontWeight: "500",
      summaryFontSize: "14px",
      iconSize: "24px",
      closeButtonWidth: "24px",
      closeButtonHeight: "24px",
      closeIconSize: "16px",
      contentPadding: "12px 16px",
      colorScheme: {
        light: {
          blur: "1.5px",
          info: {
            background: "{blue.500}",
            borderColor: "{blue.500}",
            color: "#FFFFFF",
            detailColor: "#FFFFFF",
            shadow: "0px 4px 12px 0px #00000033",
            closeButton: {
              hoverBackground: "{blue.300}",
              focusRing: {
                color: "{blue.600}",
                shadow: "none",
              },
            },
          },
          success: {
            background: "{green.500}",
            borderColor: "{green.500}",
            color: "#FFFFFF",
            detailColor: "#FFFFFF",
            shadow: "0px 4px 12px 0px #00000033",
            closeButton: {
              hoverBackground: "{green.300}",
              focusRing: {
                color: "{green.600}",
                shadow: "none",
              },
            },
          },
          warn: {
            background: "{yellow.500}",
            borderColor: "{yellow.500}",
            color: "#FFFFFF",
            detailColor: "#FFFFFF",
            shadow: "0px 4px 12px 0px #00000033",
            closeButton: {
              hoverBackground: "{yellow.300}",
              focusRing: {
                color: "{yellow.600}",
                shadow: "none",
              },
            },
          },
          error: {
            background: "{red.500}",
            borderColor: "{red.500}",
            color: "#FFFFFF",
            detailColor: "#FFFFFF",
            shadow: "0px 4px 12px 0px #00000033",
            closeButton: {
              hoverBackground: "{red.300}",
              focusRing: {
                color: "{red.600}",
                shadow: "none",
              },
            },
          },
          secondary: {
            background: "{surface.100}",
            borderColor: "{surface.200}",
            color: "{surface.600}",
            detailColor: "{surface.700}",
            shadow: "0px 4px 12px 0px #00000033",
            closeButton: {
              hoverBackground: "{surface.200}",
              focusRing: {
                color: "{surface.600}",
                shadow: "none",
              },
            },
          },
          contrast: {
            background: "{surface.900}",
            borderColor: "{surface.950}",
            color: "{surface.50}",
            detailColor: "{surface.0}",
            shadow: "0px 4px 12px 0px #00000033",
            closeButton: {
              hoverBackground: "{surface.800}",
              focusRing: {
                color: "{surface.50}",
                shadow: "none",
              },
            },
          },
        },
        dark: {
          blur: "1.5px",
          info: {
            background: "{blue.500}",
            borderColor: "{blue.500}",
            color: "#FFFFFF",
            detailColor: "#FFFFFF",
            shadow: "0px 4px 12px 0px #00000033",
            closeButton: {
              hoverBackground: "{blue.300}",
              focusRing: {
                color: "{blue.600}",
                shadow: "none",
              },
            },
          },
          success: {
            background: "{green.500}",
            borderColor: "{green.500}",
            color: "#FFFFFF",
            detailColor: "#FFFFFF",
            shadow: "0px 4px 12px 0px #00000033",
            closeButton: {
              hoverBackground: "{green.300}",
              focusRing: {
                color: "{green.600}",
                shadow: "none",
              },
            },
          },
          warn: {
            background: "{yellow.500}",
            borderColor: "{yellow.500}",
            color: "#FFFFFF",
            detailColor: "#FFFFFF",
            shadow: "0px 4px 12px 0px #00000033",
            closeButton: {
              hoverBackground: "{yellow.300}",
              focusRing: {
                color: "{yellow.600}",
                shadow: "none",
              },
            },
          },
          error: {
            background: "{red.500}",
            borderColor: "{red.500}",
            color: "#FFFFFF",
            detailColor: "#FFFFFF",
            shadow: "0px 4px 12px 0px #00000033",
            closeButton: {
              hoverBackground: "{red.300}",
              focusRing: {
                color: "{red.600}",
                shadow: "none",
              },
            },
          },
          secondary: {
            background: "{surface.100}",
            borderColor: "{surface.200}",
            color: "{surface.600}",
            detailColor: "{surface.700}",
            shadow: "0px 4px 12px 0px #00000033",
            closeButton: {
              hoverBackground: "{surface.200}",
              focusRing: {
                color: "{surface.600}",
                shadow: "none",
              },
            },
          },
          contrast: {
            background: "{surface.900}",
            borderColor: "{surface.950}",
            color: "{surface.50}",
            detailColor: "{surface.0}",
            shadow: "0px 4px 12px 0px #00000033",
            closeButton: {
              hoverBackground: "{surface.800}",
              focusRing: {
                color: "{surface.50}",
                shadow: "none",
              },
            },
          },
        },
      },
    },
    datatable: {
      columnTitle: {
        fontWeight: 500,
      },
      row: {
        color: "030712",
      },
      header: {
        padding: "12px",
        cell: {
          color: "#030712",
        },
      },
      footer: {
        border: {
          width: "1px 0 0 0",
          color: "#E8E8E8",
        },
        padding: "12px 0px",
      },
      bodyCell: {
        borderColor: "#E8E8E8",
      },
      paginator: {
        bottom: {
          borderWidth: "1px 0 0 0",
        },
      },
    },
    menu: {
      list: {
        padding: "8px",
      },
      item: {
        padding: "10px 8px",
        focus: {
          background: "{neutral.100}",
        },
        color: "#030712",
      },
    },
    paginator: {
      padding: "4px",
    },
    carousel: {
      indicator: {
        width: "6px",
        height: "6px",
        borderRadius: "50%",
        hover: {
          background: "{neutral.100}",
        },
        active: {
          background: "{neutral.600}",
        },
      },
    },
    tabs: {
      tab: {
        border: {
          width: "0px 0px 2px 0px",
        },
      },
      tabpanel: {
        padding: "0px",
      },
    },
  },
})
